import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Lightning, ChartLineUp, Trophy, WarningCircle } from "@phosphor-icons/react";
import { useWallet } from "@/lib/useWallet";

/**
 * NoCreditsModal — premium pop-up that opens whenever the backend rejects
 * a write with HTTP 402 / error="credits_exhausted".
 *
 * Subscribes to the global `agentos:credits_exhausted` event published by
 * `lib/api.js`. Mount this component once at the AppShell root and it
 * handles every blocked attempt across the whole app.
 *
 * Visual choice: 3 packs as horizontally-aligned premium cards (mirrors
 * Stripe pricing pages). The middle pack is highlighted as "Recomendado".
 * Single CTA at the bottom takes the operator to `/app/financeiro` where
 * the actual checkout lives — we never charge from inside this modal.
 */

const PACKS = [
  {
    code: "pack_5k",
    credits: "5.000",
    price: "R$ 97",
    label: "Pacote Inicial",
    blurb: "Cobre uma operação leve por aproximadamente 1-2 semanas.",
    icon: Lightning,
    accent: "text-slate-700",
  },
  {
    code: "pack_20k",
    credits: "20.000",
    price: "R$ 297",
    label: "Mais escolhido",
    blurb: "Volume médio mensal — ideal pra maioria das oficinas.",
    icon: ChartLineUp,
    accent: "text-emerald-700",
    highlight: true,
  },
  {
    code: "pack_50k",
    credits: "50.000",
    price: "R$ 697",
    label: "Pacote Operação",
    blurb: "Alto volume + margem de segurança pra picos.",
    icon: Trophy,
    accent: "text-blue-700",
  },
];

export default function NoCreditsModal() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null); // detail payload from the 402
  const { wallet } = useWallet();
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onExhaust = (e) => {
      setReason(e?.detail || null);
      setOpen(true);
    };
    window.addEventListener("agentos:credits_exhausted", onExhaust);
    return () => window.removeEventListener("agentos:credits_exhausted", onExhaust);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!open) return null;

  const goToPacks = () => {
    close();
    navigate("/app/financeiro");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8" data-testid="no-credits-modal">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* dialog */}
      <div
        className="relative bg-white rounded-lg shadow-2xl w-full max-w-3xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Sem créditos para enviar"
      >
        {/* header */}
        <div className="flex items-start justify-between px-7 py-5 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
              <WarningCircle size={18} weight="regular" />
            </div>
            <div>
              <h2 className="font-heading text-[17px] font-semibold text-slate-900 tracking-tight">
                Seus créditos do mês acabaram
              </h2>
              <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                {reason?.message ||
                  "Sem créditos suficientes pra continuar enviando mensagens, atender clientes pela IA ou rodar campanhas."}
                {wallet && (
                  <span className="block mt-1 text-[12.5px] text-slate-500">
                    Saldo atual: <b className="text-slate-700">{wallet.balance.toLocaleString("pt-BR")}</b> de {wallet.monthly_grant.toLocaleString("pt-BR")} créditos do plano base.
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={close}
            data-testid="no-credits-modal-close"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
            aria-label="Fechar"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* packs */}
        <div className="px-7 py-6 bg-slate-50">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-3">
            Compre um pacote extra agora mesmo
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PACKS.map((p) => (
              <div
                key={p.code}
                data-testid={`no-credits-pack-${p.code}`}
                className={`relative rounded-lg p-4 border transition-all ${
                  p.highlight
                    ? "bg-white border-slate-900 shadow-md ring-1 ring-slate-900/5"
                    : "bg-white border-slate-200"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-2 left-4 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[9px] uppercase tracking-[0.16em] font-semibold">
                    {p.label}
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-medium">
                  <p.icon size={11} weight="regular" className={p.accent} />
                  {!p.highlight && p.label}
                </div>
                <div className="font-heading text-[24px] font-semibold text-slate-900 mt-2 tabular-nums tracking-tight">
                  {p.price}
                </div>
                <div className="text-[12.5px] text-slate-700 font-medium mt-0.5">
                  +{p.credits} créditos
                </div>
                <div className="text-[11.5px] text-slate-500 mt-2 leading-relaxed">
                  {p.blurb}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* footer CTA */}
        <div className="px-7 py-5 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[12px] text-slate-500 leading-relaxed">
            Compras de pacote ficam disponíveis <b>imediatamente</b> após o pagamento.
            <br className="hidden sm:inline" />
            Para dúvidas, fale com nosso time pelo WhatsApp.
          </p>
          <button
            onClick={goToPacks}
            data-testid="no-credits-modal-buy-now"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-[13px] font-medium hover:bg-slate-800 transition-colors"
          >
            Ver pacotes e comprar
            <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
